import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import apiService from '../services/ApiService';
import { useAuth } from '../contexts/AuthContext';
import VehicleCard from './VehicleCard';
import { Modal } from './ui/Modal';
import { EmptyState } from './ui/EmptyState';
import { DiscoverSkeleton } from './ui/Skeletons';
import { Button } from './ui/Button';
import { PageHeader } from './ui/PageHeader';
import PageShell from './ui/PageShell';
import type { DiscoveryFilters, PaginationMeta } from '../types/discoveryBadges';
import { filtersFromParams, filtersToSearchParams } from '../utils/discoveryUrlState';
import { vehicleProfilePath } from '../utils/vehicleUrls';
import VehicleFieldCombobox from './VehicleFieldCombobox';
import type { VehicleSuggestField } from './VehicleFieldCombobox';
import { insertSponsoredCards } from '../utils/sponsoredInsertion';
import SponsoredCard from './home/SponsoredCard';
import type { AdCreative } from './home/homeTypes';

const SORT_OPTIONS = [
  { value: 'updated_at:desc', label: 'Recently updated' },
  { value: 'created_at:desc', label: 'Recently added' },
  { value: 'year:desc', label: 'Year' },
  { value: 'make:asc', label: 'Make / model' },
  { value: 'documentation_score:desc', label: 'Most documented' },
  { value: 'modification_count:desc', label: 'Most modifications' },
  { value: 'history_entries:desc', label: 'Most history entries' },
  { value: 'award_count:desc', label: 'Most awarded' },
  { value: 'event_participation_count:desc', label: 'Most event participation' },
  { value: 'verified_power:desc', label: 'Highest verified power' },
  { value: 'power_to_weight:desc', label: 'Best power to weight' },
  { value: 'horsepower:desc', label: 'Highest listed horsepower' },
  { value: 'recently_achieved:desc', label: 'Recently achieved' },
  { value: 'recently_dynoed:desc', label: 'Recently dynoed' },
  { value: 'recent_personal_best:desc', label: 'Recent personal best' },
  { value: 'favorite_count:desc', label: 'Most favorited' },
  { value: 'view_count:desc', label: 'Most viewed' },
];

const BUILD_PURPOSES: [string, string][] = [
  ['street', 'Street'],
  ['daily', 'Daily'],
  ['track', 'Track'],
  ['time_attack', 'Time attack'],
  ['drag', 'Drag'],
  ['drift', 'Drift'],
  ['rally', 'Rally'],
  ['autocross', 'Autocross'],
  ['show', 'Show'],
  ['stance', 'Stance'],
  ['off_road', 'Off-road'],
  ['overland', 'Overland'],
  ['restoration', 'Restoration'],
  ['oem_plus', 'OEM+'],
  ['touring', 'Touring'],
  ['competition', 'Competition'],
  ['mixed_use', 'Mixed use'],
];

function applyFiltersToParams(
  filters: DiscoveryFilters,
  setParams: (nextInit: URLSearchParams, navigateOpts?: { replace?: boolean }) => void
) {
  setParams(filtersToSearchParams(filters), { replace: true });
}

const DiscoverPage: React.FC = () => {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const filters = useMemo(() => filtersFromParams(params), [params]);
  const [vehicles, setVehicles] = useState<any[]>([]);
  const [pagination, setPagination] = useState<PaginationMeta>({ page: 1, limit: 20, total_count: 0, total_pages: 0 });
  const [loading, setLoading] = useState(true);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [draft, setDraft] = useState<DiscoveryFilters>(filters);
  const [searchInput, setSearchInput] = useState(filters.search || '');
  const [sponsoredCreatives, setSponsoredCreatives] = useState<AdCreative[]>([]);

  useEffect(() => {
    setDraft(filters);
    setSearchInput(filters.search || '');
  }, [filters]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [result, ad] = await Promise.all([
        apiService.discoverVehicles(filters),
        apiService.getAdPlacement('discover_native_card').catch(() => null),
      ]);
      setVehicles(result.data);
      setPagination(result.pagination);
      setHasLoadedOnce(true);
      // Stable for this page view / filter set: at most one placement fetch per load
      setSponsoredCreatives(ad ? [ad] : []);
    } catch (e: any) {
      setError(e.message || 'Failed to load builds');
      setVehicles([]);
      setSponsoredCreatives([]);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    load();
  }, [load]);

  const gridItems = useMemo(
    () =>
      insertSponsoredCards(vehicles, sponsoredCreatives, {
        after: 6,
        max: 2,
      }),
    [vehicles, sponsoredCreatives]
  );

  const activeChips = useMemo(() => {
    const chips: { key: string; label: string }[] = [];
    const add = (key: keyof DiscoveryFilters, label: string) => {
      const v = filters[key];
      if (v !== undefined && v !== null && v !== '' && v !== false) chips.push({ key: String(key), label: `${label}: ${v}` });
    };
    add('make', 'Make');
    add('model', 'Model');
    add('chassis', 'Chassis');
    add('engine', 'Engine');
    add('transmission', 'Transmission');
    add('drivetrain', 'Drivetrain');
    add('build_status', 'Status');
    add('mod_brand', 'Brand');
    add('mod_category', 'Category');
    add('event_slug', 'Event');
    add('award_title', 'Award');
    add('badge_category', 'Badge');
    add('club_slug', 'Club');
    if (filters.year_min || filters.year_max) {
      chips.push({ key: 'year', label: `Year: ${filters.year_min || '…'}-${filters.year_max || '…'}` });
    }
    if (filters.has_images === 'true' || filters.has_images === true) chips.push({ key: 'has_images', label: 'Has photos' });
    if (filters.has_awards === 'true' || filters.has_awards === true) chips.push({ key: 'has_awards', label: 'Has awards' });
    if (filters.verified_club === 'true' || filters.verified_club === true) chips.push({ key: 'verified_club', label: 'Verified club' });
    if (filters.certified === 'true' || filters.certified === true) chips.push({ key: 'certified', label: 'Certified builds' });
    if (filters.spec_key && filters.spec_value) {
      chips.push({ key: 'spec', label: `${filters.spec_key}: ${filters.spec_value}` });
    }
    if (filters.build_purpose) {
      const purpose = BUILD_PURPOSES.find(([v]) => v === filters.build_purpose);
      chips.push({ key: 'build_purpose', label: `Purpose: ${purpose ? purpose[1] : filters.build_purpose}` });
    }
    if (filters.doc_score_min) chips.push({ key: 'doc_score_min', label: `Documented ${filters.doc_score_min}+` });
    if (filters.has_verified_dyno === 'true' || filters.has_verified_dyno === true)
      chips.push({ key: 'has_verified_dyno', label: 'Verified dyno' });
    if (filters.has_performance_record === 'true' || filters.has_performance_record === true)
      chips.push({ key: 'has_performance_record', label: 'Performance records' });
    if (filters.has_achievement === 'true' || filters.has_achievement === true)
      chips.push({ key: 'has_achievement', label: 'Has achievements' });
    if (filters.has_shop_contributor === 'true' || filters.has_shop_contributor === true)
      chips.push({ key: 'has_shop_contributor', label: 'Shop built' });
    if (filters.has_albums === 'true' || filters.has_albums === true)
      chips.push({ key: 'has_albums', label: 'Has albums' });
    if (filters.power_min || filters.power_max) {
      chips.push({ key: 'power', label: `Power: ${filters.power_min || '0'}-${filters.power_max || 'any'} whp` });
    }
    if (filters.ptw_min) chips.push({ key: 'ptw_min', label: `${filters.ptw_min}+ hp/ton` });
    if (filters.achievement) chips.push({ key: 'achievement', label: `Achievement: ${filters.achievement}` });
    return chips;
  }, [filters]);

  const clearFilter = (key: string) => {
    const next = { ...filters, page: 1 };
    if (key === 'year') {
      next.year_min = undefined;
      next.year_max = undefined;
    } else if (key === 'power') {
      next.power_min = undefined;
      next.power_max = undefined;
    } else if (key === 'spec') {
      next.spec_key = undefined;
      next.spec_value = undefined;
    } else {
      (next as any)[key] = undefined;
    }
    applyFiltersToParams(next, setParams);
  };

  const clearAll = () => {
    applyFiltersToParams({ sort_by: 'updated_at', sort_order: 'desc', page: 1 }, setParams);
    setSearchInput('');
  };

  const submitSearch = (e?: React.FormEvent) => {
    e?.preventDefault();
    applyFiltersToParams({ ...filters, search: searchInput.trim() || undefined, page: 1 }, setParams);
  };

  const applyDraft = () => {
    applyFiltersToParams({ ...draft, search: searchInput.trim() || draft.search, page: 1 }, setParams);
    setSheetOpen(false);
  };

  const sortValue = `${filters.sort_by || 'updated_at'}:${filters.sort_order || 'desc'}`;
  const hasActiveFilters = activeChips.length > 0 || Boolean(filters.search?.trim());

  return (
    <PageShell>
        <PageHeader
          eyebrow="Browse"
          title="Discover"
          description="Find builds by chassis, engine, drivetrain, events, awards, and clubs."
          backLink={isAuthenticated ? { label: 'Back to Garage', to: '/garage' } : undefined}
        />

        <form onSubmit={submitSearch} className="flex flex-col sm:flex-row gap-3 mb-4">
          <label className="sr-only" htmlFor="discover-search">
            Search builds
          </label>
          <input
            id="discover-search"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search engines, chassis, make…"
            className="flex-1 input min-h-[44px]"
          />
          <div className="flex gap-2">
            <Button type="submit" variant="primary">
              Search
            </Button>
            <Button type="button" variant="secondary" onClick={() => setSheetOpen(true)}>
              Filters
            </Button>
          </div>
        </form>

        <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4">
          <div className="flex-1 flex gap-2 overflow-x-auto pb-1" aria-label="Active filters">
            {isAuthenticated ? (
              <button
                type="button"
                onClick={() => navigate('/favorites')}
                className="filter-chip whitespace-nowrap min-h-[36px]"
              >
                My favorites
              </button>
            ) : null}
            {activeChips.map((c) => (
              <button
                key={c.key}
                type="button"
                onClick={() => clearFilter(c.key)}
                className="filter-chip whitespace-nowrap min-h-[36px]"
              >
                {c.label} ×
              </button>
            ))}
            {activeChips.length > 0 ? (
              <button type="button" className="text-sm text-orange-400 hover:text-orange-300 whitespace-nowrap" onClick={clearAll}>
                Clear all
              </button>
            ) : null}
          </div>
          <div className="flex items-center gap-3 text-sm text-gray-400">
            <span aria-live="polite">{pagination.total_count} builds</span>
            <label className="flex items-center gap-2">
              <span className="sr-only">Sort</span>
              <select
                className="select-surface min-h-[44px]"
                value={sortValue}
                onChange={(e) => {
                  const [sort_by, sort_order] = e.target.value.split(':');
                  const advancedSorts = [
                    'documentation_score', 'verified_power', 'power_to_weight', 'history_entries',
                    'recently_achieved', 'recently_dynoed', 'recent_personal_best',
                  ];
                  if (advancedSorts.includes(sort_by)) {
                    apiService.trackEvent('discover_advanced_sort_applied', { metadata: { sort_by } });
                  }
                  applyFiltersToParams({ ...filters, sort_by, sort_order: sort_order as 'asc' | 'desc', page: 1 }, setParams);
                }}
              >
                {SORT_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </label>
          </div>
        </div>

        {loading ? <DiscoverSkeleton fullPage={!hasLoadedOnce} cardCount={6} /> : null}
        {error ? (
          <div role="alert" className="rounded-lg border border-red-900/50 bg-red-950/40 p-4 text-red-200 mb-4">
            {error}
            <Button type="button" variant="secondary" className="ml-3" onClick={load}>
              Retry
            </Button>
          </div>
        ) : null}

        {!loading && !error && vehicles.length === 0 ? (
          <EmptyState
            title={hasActiveFilters ? 'No builds match' : 'No public builds yet'}
            description={
              hasActiveFilters
                ? 'Try clearing a filter or searching a broader engine or chassis code.'
                : 'Published builds from the community will appear here as builders share their garages.'
            }
            action={
              hasActiveFilters ? (
                <Button type="button" variant="secondary" onClick={clearAll}>
                  Clear filters
                </Button>
              ) : (
                <Button type="button" variant="primary" onClick={() => navigate(isAuthenticated ? '/garage' : '/register')}>
                  {isAuthenticated ? 'Open your garage' : 'Create a garage'}
                </Button>
              )
            }
          />
        ) : null}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {gridItems.map((entry, index) => {
            if (entry.kind === 'sponsored') {
              return (
                <SponsoredCard
                  key={`sponsored-${entry.item.creative_id}-${index}`}
                  creative={entry.item}
                />
              );
            }
            const v = entry.item;
            return (
              <VehicleCard
                key={v.id}
                vehicle={{
                  ...v,
                  trim: v.trim || '',
                  modification_count: v.modification_count || 0,
                  display_name: v.display_name || `${v.year} ${v.make} ${v.model}`,
                  created_at: v.created_at || '',
                }}
                collectionMode
                showOwner
                onVehicleClick={(vehicle) => {
                  const path = vehicleProfilePath(
                    vehicle.owner_username,
                    vehicle.public_slug || String(vehicle.id)
                  );
                  navigate(path);
                }}
                onUserClick={(username) => navigate(`/${username}`)}
                discoveryBadges={v.badges}
                discoveryBadgeOverflow={v.badge_overflow}
              />
            );
          })}
        </div>

        {pagination.total_pages > 1 ? (
          <div className="flex justify-center gap-3 mt-8">
            <Button
              type="button"
              variant="secondary"
              disabled={pagination.page <= 1}
              onClick={() => applyFiltersToParams({ ...filters, page: pagination.page - 1 }, setParams)}
            >
              Previous
            </Button>
            <span className="text-gray-400 self-center text-sm">
              Page {pagination.page} of {pagination.total_pages}
            </span>
            <Button
              type="button"
              variant="secondary"
              disabled={pagination.page >= pagination.total_pages}
              onClick={() => applyFiltersToParams({ ...filters, page: pagination.page + 1 }, setParams)}
            >
              Next
            </Button>
          </div>
        ) : null}

      <Modal isOpen={sheetOpen} onClose={() => setSheetOpen(false)} variant="sheet" title="Filters">
        <div className="space-y-4 p-1">
          {(
            [
              ['make', 'Make'],
              ['model', 'Model'],
            ] as const
          ).map(([key, label]) => (
            <label key={key} className="block text-sm text-gray-300">
              {label}
              <input
                className="mt-1 w-full input min-h-[44px]"
                value={(draft[key] as string) || ''}
                onChange={(e) => setDraft({ ...draft, [key]: e.target.value || undefined })}
              />
            </label>
          ))}
          {(
            [
              ['chassis', 'Chassis'],
              ['engine', 'Engine'],
              ['transmission', 'Transmission'],
            ] as const
          ).map(([key, label]) => (
            <label key={key} className="block text-sm text-gray-300">
              {label}
              <VehicleFieldCombobox
                field={key as VehicleSuggestField}
                id={`discover-filter-${key}`}
                value={(draft[key] as string) || ''}
                onChange={(v) => setDraft({ ...draft, [key]: v.trim() || undefined })}
                allowCreate={false}
                placeholder={`Existing ${label.toLowerCase()} values…`}
                className="mt-1 input min-h-[44px]"
              />
            </label>
          ))}
          <fieldset>
            <legend className="text-sm text-gray-300 mb-2">Drivetrain</legend>
            <div className="flex flex-wrap gap-2">
              {['FWD', 'RWD', 'AWD', '4WD'].map((d) => (
                <button
                  key={d}
                  type="button"
                  className={draft.drivetrain === d ? 'filter-chip-active' : 'filter-chip'}
                  onClick={() => setDraft({ ...draft, drivetrain: draft.drivetrain === d ? undefined : d })}
                >
                  {d}
                </button>
              ))}
            </div>
          </fieldset>
          <fieldset>
            <legend className="text-sm text-gray-300 mb-2">Build status</legend>
            <div className="flex flex-wrap gap-2">
              {[
                ['planned', 'Planned'],
                ['in_progress', 'In progress'],
                ['complete', 'Complete'],
              ].map(([val, label]) => (
                <button
                  key={val}
                  type="button"
                  className={draft.build_status === val ? 'filter-chip-active' : 'filter-chip'}
                  onClick={() => setDraft({ ...draft, build_status: draft.build_status === val ? undefined : val })}
                >
                  {label}
                </button>
              ))}
            </div>
          </fieldset>
          <div className="grid grid-cols-2 gap-3">
            <label className="text-sm text-gray-300">
              Year min
              <input
                inputMode="numeric"
                className="mt-1 w-full input min-h-[44px]"
                value={draft.year_min ?? ''}
                onChange={(e) => setDraft({ ...draft, year_min: e.target.value || undefined })}
              />
            </label>
            <label className="text-sm text-gray-300">
              Year max
              <input
                inputMode="numeric"
                className="mt-1 w-full input min-h-[44px]"
                value={draft.year_max ?? ''}
                onChange={(e) => setDraft({ ...draft, year_max: e.target.value || undefined })}
              />
            </label>
          </div>
          <label className="flex items-center gap-2 text-sm text-gray-300 min-h-[44px]">
            <input
              type="checkbox"
              checked={draft.has_images === true || draft.has_images === 'true'}
              onChange={(e) => setDraft({ ...draft, has_images: e.target.checked ? 'true' : undefined })}
            className="checkbox-control"
            />
            Has photos
          </label>
          <label className="flex items-center gap-2 text-sm text-gray-300 min-h-[44px]">
            <input
              type="checkbox"
              checked={draft.has_awards === true || draft.has_awards === 'true'}
              onChange={(e) => setDraft({ ...draft, has_awards: e.target.checked ? 'true' : undefined })}
            className="checkbox-control"
            />
            Has published awards
          </label>
          <label className="flex items-center gap-2 text-sm text-gray-300 min-h-[44px]">
            <input
              type="checkbox"
              checked={draft.verified_club === true || draft.verified_club === 'true'}
              onChange={(e) => setDraft({ ...draft, verified_club: e.target.checked ? 'true' : undefined })}
            className="checkbox-control"
            />
            Verified club
          </label>
          <label className="flex items-center gap-2 text-sm text-gray-300 min-h-[44px]">
            <input
              type="checkbox"
              checked={draft.certified === true || draft.certified === 'true'}
              onChange={(e) => setDraft({ ...draft, certified: e.target.checked ? 'true' : undefined })}
            className="checkbox-control"
            />
            Certified builds
          </label>
          <label className="block text-sm text-gray-300">
            Mod brand
            <input
              className="mt-1 w-full input min-h-[44px]"
              value={draft.mod_brand || ''}
              onChange={(e) => setDraft({ ...draft, mod_brand: e.target.value || undefined })}
            />
          </label>
          <label className="block text-sm text-gray-300">
            Club slug
            <input
              className="mt-1 w-full input min-h-[44px]"
              value={draft.club_slug || ''}
              onChange={(e) => setDraft({ ...draft, club_slug: e.target.value || undefined })}
            />
          </label>
          <fieldset>
            <legend className="text-sm text-gray-300 mb-2">Build purpose</legend>
            <div className="flex flex-wrap gap-2">
              {BUILD_PURPOSES.map(([val, label]) => (
                <button
                  key={val}
                  type="button"
                  className={draft.build_purpose === val ? 'filter-chip-active' : 'filter-chip'}
                  onClick={() =>
                    setDraft({ ...draft, build_purpose: draft.build_purpose === val ? undefined : val })
                  }
                >
                  {label}
                </button>
              ))}
            </div>
          </fieldset>
          <label className="block text-sm text-gray-300">
            Minimum documentation score
            <select
              className="mt-1 w-full select-surface min-h-[44px] text-gray-200"
              value={String(draft.doc_score_min ?? '')}
              onChange={(e) => setDraft({ ...draft, doc_score_min: e.target.value || undefined })}
            >
              <option value="">Any</option>
              <option value="50">50+</option>
              <option value="75">75+</option>
              <option value="90">90+</option>
            </select>
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="text-sm text-gray-300">
              Power min (whp)
              <input
                inputMode="numeric"
                className="mt-1 w-full input min-h-[44px]"
                value={draft.power_min ?? ''}
                onChange={(e) => setDraft({ ...draft, power_min: e.target.value || undefined })}
              />
            </label>
            <label className="text-sm text-gray-300">
              Power max (whp)
              <input
                inputMode="numeric"
                className="mt-1 w-full input min-h-[44px]"
                value={draft.power_max ?? ''}
                onChange={(e) => setDraft({ ...draft, power_max: e.target.value || undefined })}
              />
            </label>
          </div>
          <label className="block text-sm text-gray-300">
            Minimum power to weight (hp/ton)
            <input
              inputMode="numeric"
              className="mt-1 w-full input min-h-[44px]"
              value={draft.ptw_min ?? ''}
              onChange={(e) => setDraft({ ...draft, ptw_min: e.target.value || undefined })}
            />
          </label>
          {(
            [
              ['has_verified_dyno', 'Verified dyno result'],
              ['has_performance_record', 'Has performance records'],
              ['has_achievement', 'Has achievements'],
              ['has_shop_contributor', 'Built with a shop'],
              ['has_albums', 'Has media albums'],
            ] as const
          ).map(([key, label]) => (
            <label key={key} className="flex items-center gap-2 text-sm text-gray-300 min-h-[44px]">
              <input
                type="checkbox"
                checked={draft[key] === true || draft[key] === 'true'}
                onChange={(e) => setDraft({ ...draft, [key]: e.target.checked ? 'true' : undefined })}
              className="checkbox-control"
              />
              {label}
            </label>
          ))}
          <div className="flex gap-2 pt-2 sticky bottom-0 bg-[#0a0a0a] py-3">
            <Button type="button" variant="ghost" onClick={() => setDraft({})}>
              Reset
            </Button>
            <Button type="button" variant="primary" className="flex-1" onClick={applyDraft}>
              Apply
            </Button>
          </div>
        </div>
      </Modal>
    </PageShell>
  );
};

export default DiscoverPage;